import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CHIAVE_MODO,
  CHIAVE_TEMA,
  type IdTema,
  type Modo,
  normalizzaModo,
  normalizzaTema,
  urlFonti,
} from './catalogo.js';
import { Tema } from './contesto.js';

const LINK_FONTI = 'calcio-fonti-tema';

function applicaAlDocumento(tema: IdTema, modo: Modo) {
  const root = document.documentElement;
  root.dataset.theme = tema;
  root.classList.toggle('dark', modo === 'scuro');
  localStorage.setItem(CHIAVE_TEMA, tema);
  localStorage.setItem(CHIAVE_MODO, modo);

  const url = urlFonti(tema);
  const esistente = document.getElementById(LINK_FONTI);
  if (!url) {
    esistente?.remove();
    return;
  }
  if (esistente instanceof HTMLLinkElement) {
    if (esistente.getAttribute('href') !== url) esistente.href = url;
    return;
  }
  const link = document.createElement('link');
  link.id = LINK_FONTI;
  link.rel = 'stylesheet';
  link.href = url;
  document.head.append(link);
}

function lettoDalDocumento(): { tema: IdTema; modo: Modo } {
  if (typeof document === 'undefined') return { tema: 'carta', modo: 'chiaro' };
  return {
    tema: normalizzaTema(document.documentElement.dataset.theme),
    modo: document.documentElement.classList.contains('dark') ? 'scuro' : 'chiaro',
  };
}

export function ProviderTema({ children }: { children: ReactNode }) {
  const iniziale = useMemo(lettoDalDocumento, []);
  const [tema, setTema] = useState<IdTema>(iniziale.tema);
  const [modo, setModo] = useState<Modo>(iniziale.modo);

  useEffect(() => {
    applicaAlDocumento(tema, modo);
  }, [tema, modo]);

  const valore = useMemo(
    () => ({
      tema,
      modo,
      impostaTema: (valore: string) => setTema(normalizzaTema(valore)),
      impostaModo: (valore: string) => setModo(normalizzaModo(valore)),
    }),
    [tema, modo],
  );

  return <Tema.Provider value={valore}>{children}</Tema.Provider>;
}
